
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { mapGetters } from 'vuex';
import { estateAgentService } from '@/estate-agent/EstateAgentService';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import EstateAgentLanguage from '@/estate-agent/components/EstateAgentLanguage.vue';
import LoginButton from '@/account/components/LoginButton.vue';
import ReadMore from '@/shared/components/ReadMore.vue';
import EstateAgentReviewStars from '@/estate-agent-review/components/EstateAgentReviewStars.vue';
import EstateAgentReviewStarsList from '@/estate-agent-review/components/EstateAgentReviewStarsList.vue';
import MapSearch from '@/shared/components/MapSearch.vue';
import axios from 'axios';
import { adService } from '@/ad/AdService';
import { OfferListMode } from '@/ad/models/OfferListMode';
import AdOfferList from '@/ad/components/AdOfferList.vue';
import { imageService } from '@/shared/services/ImageService';
import { config } from '@/shared/constants';

export default defineComponent({
  name: 'EstateAgentPublicProfile',
  mixins: [Form],
  inject: ['RADON_LOADING_BAR'],
  components: {
    ResizableImage,
    EstateAgentLanguage,
    LoginButton,
    ReadMore,
    EstateAgentReviewStars,
    EstateAgentReviewStarsList,
    MapSearch,
    AdOfferList,
  },
  props: {
    urlName: { type: String, default: '' },
  },
  data() {
    return {
      isEstateAgencyMode: config.IS_ESTATE_AGENCY_MODE,
      estateAgent: {
        frontUserId: null as null | number,
      } as any,
      searchCancelSource: null as any,
      bounds: null as any,
      zoom: null as null | number,
      markers: [],
      polylines: [],
      queryParams: {
        id: null as null | number,
        page: 1 as null | number,
        pageSize: 30 as null | number,
        sortBy: 'createdUtc desc' as null | string,
        type: null as null | any,
        estateFrontSubTypes: [] as any[],
        nelat: null as null | number,
        nelng: null as null | number,
        swlat: null as null | number,
        swlng: null as null | number,
        zoom: null as null | number,
        countryCode: null as null | string,
        priceFrom: null as null | number,
        priceTo: null as null | number,
        frontUserId: null as null | number,
      },
      mode: OfferListMode.estateagent,
      isInitialized: false,
    };
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    image(): null | string {
      return imageService.getImage(this.estateAgent.photoRelativeUrl, 'img/no-photo-user.png', {
        width: 335,
        height: 335,
      });
    },
  },
  watch: {
    isLoggedIn: function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.loadData();
      }
    },
    queryParams: {
      handler: function () {
        this.search();
      },
      deep: true,
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        this.estateAgent = (await estateAgentService.getByUrlName(this.urlName)).data;
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          this.$router.push({ name: '404' });
        } else {
          console.error(error);
        }
      }
    },
    cancelSearch() {
      if (this.searchCancelSource) {
        this.searchCancelSource.cancel('Start new search, stop active search');
      }
    },
    async search() {
      try {
        this.cancelSearch();
        this.searchCancelSource = axios.CancelToken.source();
        this.isLoading = true;

        const zoom = (this.$refs.map as any).getZoom();
        if (zoom) {
          this.queryParams.zoom = zoom;
        }

        const params = Object.assign({}, this.queryParams);

        params.frontUserId = this.estateAgent.frontUserId;

        if (params.type && params.type.key) {
          params.type = params.type.key;
        }
        if (params.estateFrontSubTypes && params.estateFrontSubTypes.length > 0) {
          params.estateFrontSubTypes = params.estateFrontSubTypes.map((x) => x.key);
        }
        const markerParams = Object.assign({}, params);
        markerParams.page = null;
        markerParams.pageSize = null;
        markerParams.sortBy = null;
        markerParams.id = null;
        const markerResult = await adService.getMarkers(markerParams, this.searchCancelSource.token);
        this.markers = markerResult.data.markers;
        this.polylines = markerResult.data.polylines;
        if (!this.isInitialized) {
          (this.$refs.map as any).fitBounds(this.markers);
        }
        this.isInitialized = true;
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onBoundsChanged(bounds: google.maps.LatLngBounds | null | undefined) {
      if (bounds) {
        this.queryParams.nelat = bounds.getNorthEast().lat();
        this.queryParams.nelng = bounds.getNorthEast().lng();
        this.queryParams.swlat = bounds.getSouthWest().lat();
        this.queryParams.swlng = bounds.getSouthWest().lng();
        this.queryParams.page = 1;
      }
    },
    onMapSelected(ad: any) {
      this.goToAd(ad);
    },
    goToAd(ad: any) {
      this.$router.push({ name: 'AdDetail', params: { adFrontId: ad.adFrontId } });
    },
    scrollToReviews() {
      const el = this.$el.parentNode.getElementsByClassName('section--rating')[0];

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    async translate(showTranslation) {
      const params = {} as any;
      params.languageId = 'en';
      const resultDescription = await estateAgentService.getDescription(this.estateAgent.frontUserId, params);
      this.estateAgent.descriptionEn = resultDescription.data.description;
      showTranslation();
    },
    async translateMotto(showTranslation) {
      const params = {} as any;
      params.languageId = 'en';
      const resultMotto = await estateAgentService.getMotto(this.estateAgent.frontUserId, params);
      this.estateAgent.mottoEn = resultMotto.data.motto;
      showTranslation();
    },
  },
});
